import { supabase } from '../lib/supabase';
import openai from './openaiClient';



/**
 * Automation Rules Service
 * Handles conditional if-then workflow rules for contracts
 */

export const automationRulesService = {
  /**
   * Create a new automation rule
   */
  async createRule(ruleData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create rule
      const { data: rule, error: ruleError } = await supabase
        ?.from('automation_rules')
        ?.insert({
          user_id: user?.id,
          rule_name: ruleData?.ruleName,
          description: ruleData?.description,
          trigger_type: ruleData?.triggerType,
          is_active: ruleData?.isActive ?? true,
          priority: ruleData?.priority || 0
        })
        ?.select()
        ?.single();

      if (ruleError) throw ruleError;

      // Create conditions
      if (ruleData?.conditions?.length > 0) {
        const conditionsData = ruleData?.conditions?.map(condition => ({
          rule_id: rule?.id,
          condition_field: condition?.field,
          condition_operator: condition?.operator,
          condition_value: condition?.value,
          logical_operator: condition?.logicalOperator || 'AND'
        }));

        const { error: conditionsError } = await supabase
          ?.from('rule_conditions')
          ?.insert(conditionsData);

        if (conditionsError) throw conditionsError;
      }

      // Create actions
      if (ruleData?.actions?.length > 0) {
        const actionsData = ruleData?.actions?.map((action, index) => ({
          rule_id: rule?.id,
          action_type: action?.type,
          action_config: action?.config || {},
          execution_order: index + 1
        }));

        const { error: actionsError } = await supabase
          ?.from('rule_actions')
          ?.insert(actionsData);

        if (actionsError) throw actionsError;
      }

      return await this.getRuleById(rule?.id);
    } catch (error) {
      console.error('Error creating automation rule:', error);
      throw error;
    }
  },

  /**
   * Get all automation rules for current user
   */
  async getAllRules(filters = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        ?.from('automation_rules')
        ?.select(`
          *,
          rule_conditions(*),
          rule_actions(*)
        `)
        ?.eq('user_id', user?.id)
        ?.order('priority', { ascending: false });

      if (filters?.isActive !== undefined) {
        query = query?.eq('is_active', filters?.isActive);
      }

      if (filters?.triggerType) {
        query = query?.eq('trigger_type', filters?.triggerType);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(rule => ({
        id: rule?.id,
        ruleName: rule?.rule_name,
        description: rule?.description,
        triggerType: rule?.trigger_type,
        isActive: rule?.is_active,
        priority: rule?.priority,
        conditions: rule?.rule_conditions?.map(c => ({
          id: c?.id,
          field: c?.condition_field,
          operator: c?.condition_operator,
          value: c?.condition_value,
          logicalOperator: c?.logical_operator
        })),
        actions: rule?.rule_actions?.sort((a, b) => a?.execution_order - b?.execution_order)?.map(a => ({
          id: a?.id,
          type: a?.action_type,
          config: a?.action_config,
          executionOrder: a?.execution_order
        })),
        createdAt: rule?.created_at,
        updatedAt: rule?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching automation rules:', error);
      throw error;
    }
  },

  /**
   * Get single rule by ID
   */
  async getRuleById(ruleId) {
    try {
      const { data, error } = await supabase
        ?.from('automation_rules')
        ?.select(`
          *,
          rule_conditions(*),
          rule_actions(*)
        `)
        ?.eq('id', ruleId)
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        ruleName: data?.rule_name,
        description: data?.description,
        triggerType: data?.trigger_type,
        isActive: data?.is_active,
        priority: data?.priority,
        conditions: data?.rule_conditions?.map(c => ({
          id: c?.id,
          field: c?.condition_field,
          operator: c?.condition_operator,
          value: c?.condition_value,
          logicalOperator: c?.logical_operator
        })),
        actions: data?.rule_actions?.sort((a, b) => a?.execution_order - b?.execution_order)?.map(a => ({
          id: a?.id,
          type: a?.action_type,
          config: a?.action_config,
          executionOrder: a?.execution_order
        })),
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching rule:', error);
      throw error;
    }
  },

  /**
   * Update automation rule
   */
  async updateRule(ruleId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('automation_rules')
        ?.update({
          rule_name: updates?.ruleName,
          description: updates?.description,
          trigger_type: updates?.triggerType,
          is_active: updates?.isActive,
          priority: updates?.priority
        })
        ?.eq('id', ruleId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating rule:', error);
      throw error;
    }
  },

  /**
   * Delete automation rule
   */
  async deleteRule(ruleId) {
    try {
      const { error } = await supabase
        ?.from('automation_rules')
        ?.delete()
        ?.eq('id', ruleId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting rule:', error);
      throw error;
    }
  },

  /**
   * Toggle rule active status
   */
  async toggleRuleStatus(ruleId, isActive) {
    try {
      const { data, error } = await supabase
        ?.from('automation_rules')
        ?.update({ is_active: isActive })
        ?.eq('id', ruleId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error toggling rule status:', error);
      throw error;
    }
  },

  /**
   * Evaluate conditions against entity data
   */
  evaluateConditions(conditions, entityData) {
    if (!conditions || conditions?.length === 0) return true;

    let result = true;
    let currentLogicalOp = 'AND';

    for (const condition of conditions) {
      const fieldValue = this.getNestedValue(entityData, condition?.field);
      const conditionValue = condition?.value;
      let conditionMet = false;

      switch (condition?.operator) {
        case 'equals':
          conditionMet = fieldValue == conditionValue;
          break;
        case 'not_equals':
          conditionMet = fieldValue != conditionValue;
          break;
        case 'greater_than':
          conditionMet = parseFloat(fieldValue) > parseFloat(conditionValue);
          break;
        case 'less_than':
          conditionMet = parseFloat(fieldValue) < parseFloat(conditionValue);
          break;
        case 'contains':
          conditionMet = String(fieldValue)?.toLowerCase()?.includes(String(conditionValue)?.toLowerCase());
          break;
        case 'in_list':
          const list = Array.isArray(conditionValue) ? conditionValue : [conditionValue];
          conditionMet = list?.includes(fieldValue);
          break;
        default:
          conditionMet = false;
      }

      // Apply logical operator
      if (currentLogicalOp === 'AND') {
        result = result && conditionMet;
      } else if (currentLogicalOp === 'OR') {
        result = result || conditionMet;
      }

      currentLogicalOp = condition?.logicalOperator || 'AND';
    }

    return result;
  },

  /**
   * Get nested value from object using dot notation
   */
  getNestedValue(obj, path) {
    return path?.split('.')?.reduce((current, key) => current?.[key], obj);
  },

  /**
   * Execute rule actions
   */
  async executeActions(actions, entityData, ruleId) {
    const executedActions = [];

    for (const action of actions) {
      try {
        let result;

        switch (action?.type) {
          case 'assign_contract_type':
            result = await this.assignContractType(entityData, action?.config);
            break;
          case 'schedule_followup':
            result = await this.scheduleFollowUp(entityData, action?.config);
            break;
          case 'generate_document':
            result = await this.generateDocument(entityData, action?.config);
            break;
          case 'send_notification':
            result = await this.sendNotification(entityData, action?.config);
            break;
          case 'auto_approve':
            result = await this.autoApprove(entityData, action?.config);
            break;
          case 'apply_fee_structure':
            result = await this.applyFeeStructure(entityData, action?.config);
            break;
          default:
            result = { status: 'skipped', message: 'Unknown action type' };
        }

        executedActions?.push({
          action: action?.type,
          status: 'success',
          result
        });
      } catch (error) {
        executedActions?.push({
          action: action?.type,
          status: 'failed',
          error: error?.message
        });
      }
    }

    return executedActions;
  },

  /**
   * Action: Assign contract type
   */
  async assignContractType(entityData, config) {
    // Update contract with specified type and fee
    const { data, error } = await supabase
      ?.from('contracts')
      ?.update({
        contract_type: config?.contract_type,
        amount: config?.fee_percentage ? entityData?.contract_value * (config?.fee_percentage / 100) : entityData?.amount
      })
      ?.eq('id', entityData?.id)
      ?.select()
      ?.single();

    if (error) throw error;
    return { contractType: config?.contract_type, updated: true };
  },

  /**
   * Action: Schedule follow-up
   */
  async scheduleFollowUp(entityData, config) {
    const followUpDate = new Date();
    followUpDate?.setDate(followUpDate?.getDate() + (config?.days_ahead || 7));

    const { data, error } = await supabase
      ?.from('scheduled_tasks')
      ?.insert({
        user_id: entityData?.user_id,
        task_name: config?.message || 'متابعة عقد تلقائية',
        task_type: 'contract_followup',
        frequency: 'once',
        schedule_config: {
          contract_id: entityData?.id,
          message: config?.message
        },
        next_run_at: followUpDate?.toISOString()
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return { scheduled: true, followUpDate: followUpDate?.toISOString() };
  },

  /**
   * Action: Generate document using AI
   */
  async generateDocument(entityData, config) {
    try {
      const prompt = `قم بإنشاء ${config?.template || 'عقد'} باللغة العربية للعميل ${entityData?.clientName} للعقار ${entityData?.propertyTitle}. القيمة: ${entityData?.contract_value || entityData?.price} دينار أردني.`;

      const completion = await openai?.chat?.completions?.create({
        model: 'gpt-4',
        messages: [
          { role: 'system', content: 'أنت خبير في صياغة العقود العقارية باللغة العربية.' },
          { role: 'user', content: prompt }
        ],
        temperature: 0.7,
        max_tokens: 2000
      });

      const documentContent = completion?.choices?.[0]?.message?.content;

      return { generated: true, content: documentContent, template: config?.template };
    } catch (error) {
      console.error('Error generating document:', error);
      throw error;
    }
  },

  /**
   * Action: Send notification
   */
  async sendNotification(entityData, config) {
    const { data, error } = await supabase
      ?.from('notifications')
      ?.insert({
        user_id: entityData?.user_id,
        title: config?.message || 'إشعار تلقائي',
        message: `${config?.message} - العقد: ${entityData?.contractNumber || entityData?.id}`,
        type: 'automation',
        priority: config?.priority || 'medium',
        is_read: false
      })
      ?.select()
      ?.single();

    if (error) {
      console.warn('Notification table may not exist:', error);
      return { sent: false, reason: 'notification_table_missing' };
    }

    return { sent: true, notificationId: data?.id };
  },

  /**
   * Action: Auto-approve contract
   */
  async autoApprove(entityData, config) {
    const { data, error } = await supabase
      ?.from('contracts')
      ?.update({
        status: 'approved',
        approval_notes: config?.reason || 'موافقة تلقائية بناءً على قواعد الأتمتة'
      })
      ?.eq('id', entityData?.id)
      ?.select()
      ?.single();

    if (error) throw error;
    return { approved: true, reason: config?.reason };
  },

  /**
   * Action: Apply fee structure
   */
  async applyFeeStructure(entityData, config) {
    const feeAmount = config?.fee_type === 'percentage'
      ? entityData?.contract_value * (config?.fee_value / 100)
      : config?.fee_value;

    const { data, error } = await supabase
      ?.from('contracts')
      ?.update({ amount: feeAmount })
      ?.eq('id', entityData?.id)
      ?.select()
      ?.single();

    if (error) throw error;
    return { applied: true, feeAmount };
  },

  /**
   * Trigger rule evaluation for an entity
   */
  async triggerRules(triggerType, entityData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get active rules for this trigger type
      const rules = await this.getAllRules({
        isActive: true,
        triggerType
      });

      const results = [];

      for (const rule of rules) {
        // Evaluate conditions
        const conditionsMet = this.evaluateConditions(rule?.conditions, entityData);

        // Execute actions if conditions met
        let actionsExecuted = [];
        let executionResult = 'شروط غير مستوفاة';

        if (conditionsMet) {
          actionsExecuted = await this.executeActions(rule?.actions, entityData, rule?.id);
          executionResult = 'تم تنفيذ القاعدة بنجاح';
        }

        // Log execution
        await supabase
          ?.from('rule_execution_log')
          ?.insert({
            rule_id: rule?.id,
            trigger_entity_id: entityData?.id,
            trigger_entity_type: 'contract',
            conditions_met: conditionsMet,
            actions_executed: actionsExecuted,
            execution_result: executionResult
          });

        results?.push({
          ruleId: rule?.id,
          ruleName: rule?.ruleName,
          conditionsMet,
          actionsExecuted
        });
      }

      return results;
    } catch (error) {
      console.error('Error triggering rules:', error);
      throw error;
    }
  },

  /**
   * Get execution logs for a rule
   */
  async getRuleExecutionLogs(ruleId, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('rule_execution_log')
        ?.select('*')
        ?.eq('rule_id', ruleId)
        ?.order('executed_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      return data?.map(log => ({
        id: log?.id,
        triggerEntityId: log?.trigger_entity_id,
        triggerEntityType: log?.trigger_entity_type,
        conditionsMet: log?.conditions_met,
        actionsExecuted: log?.actions_executed,
        executionResult: log?.execution_result,
        errorMessage: log?.error_message,
        executedAt: log?.executed_at
      }));
    } catch (error) {
      console.error('Error fetching execution logs:', error);
      throw error;
    }
  },

  /**
   * Get rule statistics
   */
  async getRuleStatistics(ruleId) {
    try {
      const { data, error } = await supabase
        ?.from('rule_execution_log')
        ?.select('conditions_met, actions_executed')
        ?.eq('rule_id', ruleId);

      if (error) throw error;

      const totalExecutions = data?.length || 0;
      const successfulExecutions = data?.filter(log => log?.conditions_met)?.length || 0;
      const failedActions = data?.reduce((count, log) => {
        const failed = log?.actions_executed?.filter(a => a?.status === 'failed')?.length || 0;
        return count + failed;
      }, 0);

      return {
        totalExecutions,
        successfulExecutions,
        failedActions,
        successRate: totalExecutions > 0 ? (successfulExecutions / totalExecutions * 100)?.toFixed(1) : 0
      };
    } catch (error) {
      console.error('Error fetching rule statistics:', error);
      throw error;
    }
  }
};